import * as React from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import { toast } from "react-toastify";
import apiClient from "api";
import { gettext } from "i18n";
import { useModalContext } from "state/modal";
import { useStationsAndDashboards } from "state/stationsAndDashboards";
import { sortByKey } from "utils/formatting";
import {
  DASHBOARD_TYPE_REGULAR,
  DASHBOARD_TYPE_REPORTING,
} from "utils/widgets";

interface CopyWidgetToDashboardModalProps {
  dashboardType:
    | typeof DASHBOARD_TYPE_REGULAR
    | typeof DASHBOARD_TYPE_REPORTING;
  sourceDashboardId: number;
  stationIdentifier: string;
  rowIndex: number;
  widgetIndex: number;
  onCopied?: (targetDashboardId: number) => void;
}

const CopyWidgetToDashboardModal = ({
  dashboardType,
  sourceDashboardId,
  stationIdentifier,
  rowIndex,
  widgetIndex,
  onCopied,
}: CopyWidgetToDashboardModalProps) => {
  const { setActiveModal } = useModalContext();
  const { dashboards, reportingDashboards } = useStationsAndDashboards();
  const [submitDisabled, setSubmitDisabled] = React.useState<boolean>(false);
  const [targetDashboardId, setTargetDashboardId] = React.useState<string>(
    String(sourceDashboardId),
  );

  const candidateDashboards = React.useMemo(() => {
    const list =
      dashboardType === DASHBOARD_TYPE_REPORTING
        ? (reportingDashboards ?? [])
        : (dashboards ?? []);
    return sortByKey(list, "name");
  }, [dashboardType, dashboards, reportingDashboards]);

  const onHide = () => setActiveModal(null);

  const copyWidget = async () => {
    if (!targetDashboardId || submitDisabled) {
      return;
    }
    setSubmitDisabled(true);
    try {
      await apiClient.request("/organization-admin/copy-dashboard-widget", {
        method: "POST",
        data: {
          dashboardType:
            dashboardType === DASHBOARD_TYPE_REPORTING
              ? "reporting_dashboard"
              : "dashboard",
          sourceDashboardId: Number(sourceDashboardId),
          // Key the backend permission predicate resolves the target
          // dashboard's organization from
          ...(dashboardType === DASHBOARD_TYPE_REPORTING
            ? { reportingDashboardId: Number(targetDashboardId) }
            : { dashboardId: Number(targetDashboardId) }),
          stationIdentifier,
          rowIndex,
          widgetIndex,
        },
      });
      toast.success(gettext("Widget copied to dashboard"));
      onCopied?.(Number(targetDashboardId));
      onHide();
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${gettext("Failed to copy widget")} ${gettext(e.toString())}`,
      );
    }
    setSubmitDisabled(false);
  };

  return (
    <Modal
      show
      onHide={onHide}
      size="lg"
      aria-labelledby="copy-widget-modal-title"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="copy-widget-modal-title">
          {gettext("Copy widget to dashboard")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table responsive>
          <tbody>
            <tr>
              <th>{gettext("Target dashboard")}</th>
              <td>
                <Form.Select
                  onChange={(e) => setTargetDashboardId(e.target.value)}
                  value={targetDashboardId}
                >
                  <option value="" disabled>
                    {gettext("Select a dashboard")}
                  </option>
                  {candidateDashboards.map((dashboard: any) => (
                    <option key={dashboard.id} value={String(dashboard.id)}>
                      {dashboard.name}
                      {Number(dashboard.id) === Number(sourceDashboardId)
                        ? ` (${gettext("this dashboard")})`
                        : ""}
                    </option>
                  ))}
                </Form.Select>
              </td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>{gettext("Cancel")}</Button>
        <Button
          type="button"
          onClick={copyWidget}
          disabled={submitDisabled || !targetDashboardId}
        >
          {gettext("Copy")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CopyWidgetToDashboardModal;
